import React, { useState } from 'react';
import { MapContainer, TileLayer } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

import './icons';
import { DEFAULT_CENTER, DEFAULT_ZOOM, resolveLayer } from './layers';
import AvisFond from './AvisFond';
import { FOND_DE_REPLI, useSuiviTuiles } from './tiles';
import { AutoInvalidateSize, MapClickHandler, MapFitBounds, MapFocus } from './controls';

const EMPTY_ARRAY = [];

/**
 * The single map surface of the product.
 *
 * Five screens used to instantiate `MapContainer` themselves, each with its
 * own tile URLs, its own icon fix and its own recentring logic. Everything
 * that is true of *every* map — basemap, size invalidation, framing, focus —
 * lives here once; what a screen actually plots is passed as children.
 */
export default function MapSurface({
  layer = 'satellite',
  center = DEFAULT_CENTER,
  zoom = DEFAULT_ZOOM,
  fitTo = EMPTY_ARRAY,
  fitEnabled = true,
  fitOnce = true,
  focus = null,
  focusZoom,
  focusMinZoom = 12,
  focusAnimate = true,
  onMapClick,
  legend = null,
  overlay = null,
  className = '',
  mapClassName = 'h-full w-full',
  scrollWheelZoom = true,
  zoomControl = true,
  children,
}) {
  // Fond indisponible (voir tiles.js): pastille « Réessayer / Passer au plan ».
  const suivi = useSuiviTuiles();
  const [repli, setRepli] = useState(null);
  const fond = repli === layer ? FOND_DE_REPLI : layer;
  const config = resolveLayer(fond);
  const passerAuPlan = () => {
    setRepli(layer);
    suivi.reinitialiser();
  };

  return (
    <div className={`relative h-full w-full ${className}`}>
      {legend}
      {overlay}
      {suivi.indisponible && (
        <AvisFond
          onReessayer={suivi.reessayer}
          onPasserAuPlan={config !== resolveLayer(FOND_DE_REPLI) ? passerAuPlan : null}
        />
      )}
      <MapContainer
        center={center}
        zoom={zoom}
        maxZoom={config.maxZoom}
        scrollWheelZoom={scrollWheelZoom}
        zoomControl={zoomControl}
        className={`${mapClassName} z-0`}
      >
        {/* Keying on the preset forces a fresh tile layer on switch instead of
            leaving Leaflet to reconcile two different URL templates. */}
        <TileLayer
          key={`${fond}-${suivi.essai}`}
          url={config.url}
          attribution={config.attribution}
          maxZoom={config.maxZoom}
          eventHandlers={suivi.gestionnaires}
        />
        {config.labels && (
          <TileLayer
            key={`${fond}-${suivi.essai}-labels`}
            url={config.labels.url}
            opacity={config.labels.opacity}
            maxZoom={config.labels.maxZoom || config.maxZoom}
          />
        )}

        <AutoInvalidateSize />
        <MapFitBounds points={fitTo} enabled={fitEnabled && !focus} once={fitOnce} />
        {focus && (
          <MapFocus
            target={focus}
            zoom={focusZoom}
            minZoom={focusMinZoom}
            animate={focusAnimate}
          />
        )}
        {onMapClick && <MapClickHandler onClick={onMapClick} />}

        {children}
      </MapContainer>
    </div>
  );
}
